import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { format, parseISO, isValid } from "date-fns";
import { Paperclip, ChevronLeft } from "lucide-react";
import {
	setIsViewingEmail,
	viewEmail,
	clearNewEmailAnimation,
	setChannel,
	setBrowserUrl,
	setAppUrl,
	setDxaProps,
} from "../features/channelSlice";
import { getColorForLetter } from "../utils/colorUtils";
import { detectUrlType, transformMagicUrlToDxa } from "../utils/urlDetector";
import { getNextAssignmentForCase } from "../utils/pegaApi";

function renderEmailBody(html, dispatch, brandConfig) {
	try {
		// Validate input
		if (!html || typeof html !== 'string') {
			console.warn('⚠️ Invalid email body: not a string', typeof html);
			return <p className="text-gray-500">Unable to display email content</p>;
		}

		const template = brandConfig?.channelBranding?.email?.template ?? '{body}';
		const parser = new window.DOMParser();
		const doc = parser.parseFromString(template.replace(/{body}/, html.replaceAll('\\n', '<br/>')), "text/html");
		
		// Check for parsing errors
		if (doc.body === null) {
			console.error('❌ DOMParser failed to create document body');
			return <p className="text-gray-500">Unable to display email content</p>;
		}
		
		const handleLinkClick = async (href) => {
			if (!href) return;
			
			const urlType = detectUrlType(href);
			
			console.log('📎 Link clicked in email:', href, 'Type:', urlType);
			
			if (urlType === 'dxa-proxy') {
				// Magic URL detected - transform to DXA and open in AppView
				console.log('🔮 Magic DXA URL detected, transforming...');
				
				// Check if brand config is available
				if (!brandConfig) {
					console.error('❌ Brand config not loaded yet');
					console.warn('💡 User needs to configure the app via Settings modal');
					console.warn('💡 Falling back to opening URL in browser');
					dispatch(setBrowserUrl(href));
					dispatch(setChannel('browser'));
					return;
				}
				
				let dxaConfig = transformMagicUrlToDxa(href, brandConfig);

				if (dxaConfig) {
					// If openAssignment was requested but no assignmentId provided, resolve it from the case
					if (dxaConfig.action === 'openAssignment' && !dxaConfig.assignmentId && dxaConfig.caseId) {
						console.log('🔍 openAssignment without assignmentId — resolving from case:', dxaConfig.caseId);
						const configId = parseInt(localStorage.getItem('selectedConfigId') ?? '-1');
						const assignmentId = await getNextAssignmentForCase(dxaConfig.caseId, configId);
						if (assignmentId) {
							console.log('✅ Resolved assignmentId:', assignmentId);
							dxaConfig = { ...dxaConfig, assignmentId, caseId: undefined };
						} else {
							console.warn('⚠️ Could not resolve assignmentId — proceeding with caseId only');
						}
					}
					console.log('✅ Transformed to DXA config:', dxaConfig);
					dispatch(setDxaProps(dxaConfig));
					dispatch(setChannel('app'));
				} else {
					console.error('❌ Failed to transform magic URL');
					console.warn('💡 This may be due to missing or invalid DXA config in brand');
					// Fallback to browser
					dispatch(setBrowserUrl(href));
					dispatch(setChannel('browser'));
				}
			} else if (urlType === 'dxa') {
				// Full DXA URL - open in AppView with URL as-is (preserving all params)
				console.log('🎯 Full DXA URL detected, opening in AppView with explicit URL');
				// Use setAppUrl to preserve ALL URL parameters including credentials
				dispatch(setAppUrl(href));
				dispatch(setChannel('app'));
			} else {
				// Regular URL - open in browser
				console.log('🌐 Regular URL, opening in browser');
				dispatch(setBrowserUrl(href));
				dispatch(setChannel('browser'));
			}
		};
		
		const parseStyleAttribute = (styleStr) => {
			if (!styleStr) return {};
			const styles = {};
			const stylePairs = styleStr.split(';').filter(s => s.trim());
			stylePairs.forEach(pair => {
				const [key, value] = pair.split(':').map(s => s.trim());
				if (key && value) {
					const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
					styles[camelKey] = value;
				}
			});
			return styles;
		};
		
		const walk = (node, key = 0) => {
			try {
				// Handle text nodes
				if (node.nodeType === 3) {
					const text = node.textContent;
					return text && text.trim() ? text : null;
				}
				
				// Ignore non-element nodes
				if (node.nodeType !== 1) return null;
				
				// Skip script tags (security risk)
				if (node.tagName === 'SCRIPT') {
					console.warn('⚠️ Skipping potentially unsafe tag:', node.tagName);
					return null;
				}
				
				// Note: We allow STYLE tags - they're safe in email context and needed for styling
				
				// Handle img tags - skip if src is relative or missing
				if (node.tagName === "IMG") {
					const src = node.getAttribute("src");
					if (!src) {
						console.warn('⚠️ Skipping image with missing src');
						return null;
					}
					// Skip relative URLs (common cause of errors)
					if (!src.startsWith('http://') && !src.startsWith('https://') && !src.startsWith('data:')) {
						console.warn('⚠️ Skipping image with relative path:', src);
						return null;
					}
					
					// Preserve original image attributes from HTML
					const alt = node.getAttribute("alt") || "Email image";
					const width = node.getAttribute("width");
					const height = node.getAttribute("height");
					const styleAttr = node.getAttribute("style") || "";
					
					console.log('✅ Rendering image:', src);
					
					// Build inline style, preserving original styles and ensuring max-width
					const baseStyle = {
						maxWidth: '100%',
						height: 'auto',
						...parseStyleAttribute(styleAttr)
					};
					
					// If original had height attribute, preserve it (but style attribute takes precedence)
					if (height && !styleAttr.includes('height')) {
						baseStyle.height = height.endsWith('px') ? height : height + 'px';
					}
					
					// If original had width attribute, preserve it (but style attribute takes precedence)
					if (width && !styleAttr.includes('width')) {
						baseStyle.width = width.endsWith('px') ? width : width + 'px';
					}
					
					return React.createElement('img', {
						key,
						src: src,
						alt: alt,
						width: width || undefined,
						height: height || undefined,
						style: baseStyle,
						onError: (e) => {
							console.warn('⚠️ Image failed to load:', src);
						}
					});
				}
				
				// Handle links with proper href validation
				if (node.tagName === "A") {
					const href = node.getAttribute("href");
					if (!href) {
						// No href, just render text content
						return (
							<span key={key}>
								{Array.from(node.childNodes).map((child, i) => walk(child, i))}
							</span>
						);
					}
					
					// Get existing link styles from HTML
					const linkStyleAttr = node.getAttribute("style") || "";
					const linkInlineStyle = parseStyleAttribute(linkStyleAttr);
					
					// Only apply default link styling if no style attribute exists
					const linkStyle = linkStyleAttr ? linkInlineStyle : { 
						color: "#2563eb", 
						textDecoration: "underline", 
						cursor: "pointer" 
					};
					
					return (
						<a
							key={key}
							href={href}
							style={linkStyle}
							onClick={e => {
								e.preventDefault();
								handleLinkClick(href);
							}}
						>
							{Array.from(node.childNodes).map((child, i) => walk(child, i))}
						</a>
					);
				}
				
				// For other elements, preserve tag and children
				const Tag = node.tagName.toLowerCase();

				if (Tag === 'svg' || node.ownerSVGElement) {
					// transform SVG to React element, preserving all attributes and children
					return React.createElement(Tag, {
						key,
						...Array.from(node.attributes).reduce((attrs, attr) => {
							attrs[attr.name] = attr.name === 'style' ? parseStyleAttribute(attr.value) : attr.value;
							return attrs;
						}, {})
					}, Array.from(node.childNodes).map((child, i) => walk(child, i)).filter(c => c !== null));
				}
				const children = Array.from(node.childNodes).map((child, i) => walk(child, i)).filter(c => c !== null);
				
				// Get inline styles from the element
				const styleAttr = node.getAttribute("style") || "";
				const inlineStyle = parseStyleAttribute(styleAttr);
				
				// Self-closing tags (but img is already handled above)
				if (['br', 'hr', 'input'].includes(Tag)) {
					return React.createElement(Tag, { key });
				}
				
				// For container tags, always render even if empty, preserving styles
				const containerTags = ['p', 'div', 'span', 'table', 'tbody', 'tr', 'td', 'thead', 'th', 'blockquote', 'section', 'article', 'style'];
				if (containerTags.includes(Tag)) {
					return React.createElement(Tag, { key, style: inlineStyle }, children.length > 0 ? children : null);
				}
				
				// Regular tags - skip empty non-container tags, unless part of SVG
				if (children.length === 0) {
					return null;
				}
				
				return React.createElement(Tag, { key, style: inlineStyle }, children);
			} catch (err) {
				console.error('❌ Error rendering email body element:', err);
				return null;
			}
		};
		
		const rendered = Array.from(doc.body.childNodes).map((node, i) => walk(node, i)).filter(c => c !== null);
		
		return rendered.length > 0 ? rendered : <p className="text-gray-500">Email body is empty</p>;
	} catch (err) {
		console.error('❌ Error rendering email body:', err);
		return <p className="text-gray-500">Unable to display email content</p>;
	}
}

// EmailDetailView component removed - was not being used

export default function EmailView() {
	const dispatch = useDispatch();
	const {
		emails,
		isViewingEmail,
		newlyReceivedEmailId,
		followMode,
		selectedEmailId,
		channelConfig,
		currentChannel,
	} = useSelector((state) => state.channel);
	const brandConfig = useSelector((state) => state.channel.currentBrand);
	const emailListRef = useRef(null);

	// Pending follow email state for future use
	// const [pendingFollowEmailId, setPendingFollowEmailId] = useState(null);

	const meta = channelConfig[currentChannel] || {};

	// Drag-to-scroll logic
	const isDragging = useRef(false);
	const startY = useRef(0);
	const scrollTop = useRef(0);

	const handleMouseDown = (e) => {
		if (!emailListRef.current) return; // Early exit if ref is null
		
		if (emailListRef.current.scrollTop > 0) {
			e.preventDefault();
		}
		isDragging.current = true;
		const y = e.touches ? e.touches[0].clientY : e.clientY;
		startY.current = y;
		scrollTop.current = emailListRef.current.scrollTop;
		emailListRef.current.style.cursor = "grabbing";
		emailListRef.current.style.userSelect = "none";
	};

	const handleMouseMove = (e) => {
		if (!isDragging.current || !emailListRef.current) return;
		e.preventDefault();
		const y = e.touches ? e.touches[0].clientY : e.clientY;
		const walk = y - startY.current;
		emailListRef.current.scrollTop = scrollTop.current - walk;
	};

	const handleMouseUp = () => {
		isDragging.current = false;
		if (emailListRef.current) {
			emailListRef.current.style.cursor = "grab";
			emailListRef.current.style.userSelect = "auto";
		}
	};

	useEffect(() => {
		const scrollable = emailListRef.current;
		if (scrollable) {
			scrollable.addEventListener("mousedown", handleMouseDown);
			window.addEventListener("mousemove", handleMouseMove);
			window.addEventListener("mouseup", handleMouseUp);
			scrollable.addEventListener("touchstart", handleMouseDown, {
				passive: true,
			});
			window.addEventListener("touchmove", handleMouseMove);
			window.addEventListener("touchend", handleMouseUp);
		}

		return () => {
			if (scrollable) {
				scrollable.removeEventListener("mousedown", handleMouseDown);
			}
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
			window.removeEventListener("touchmove", handleMouseMove);
			window.removeEventListener("touchend", handleMouseUp);
		};
	}, [isViewingEmail]); // Re-bind if the view changes

	// Reset to inbox view when EmailView component mounts (when switching to email channel)
	useEffect(() => {
		console.log('📧 EmailView mounted - resetting to inbox view');
		dispatch(setIsViewingEmail(false));
	}, [dispatch]); // Run once on mount with dispatch dependency

	// --- UPDATED LOGIC FOR FOLLOW MODE ---
	useEffect(() => {
		if (followMode && newlyReceivedEmailId) {
			// Always return to inbox view first
			dispatch(setIsViewingEmail(false));

			// Wait for the inbox view to render, then run animation and open email
			const inboxTimer = setTimeout(() => {
				const newEmailElement = emailListRef.current?.querySelector(
					`[data-email-id="${newlyReceivedEmailId}"]`
				);

				if (newEmailElement) {
					newEmailElement.scrollIntoView({ behavior: "smooth", block: "center" });
					newEmailElement.classList.add("bg-blue-200");

					const openTimer = setTimeout(() => {
						dispatch(viewEmail(newlyReceivedEmailId));
					}, 1200);

					const cleanupTimer = setTimeout(() => {
						dispatch(clearNewEmailAnimation());
					}, 2000);

					return () => {
						clearTimeout(openTimer);
						clearTimeout(cleanupTimer);
						newEmailElement.classList.remove("bg-blue-200");
					};
				} else {
					dispatch(clearNewEmailAnimation());
				}
			}, 100); // Short delay to ensure inbox view is rendered

			return () => {
				clearTimeout(inboxTimer);
			};
		}
	}, [newlyReceivedEmailId, followMode, dispatch]);

	// Sort emails by date, newest first, handling invalid dates
	const sortedEmails = [...emails].sort((a, b) => {
		const dateA = new Date(a.date);
		const dateB = new Date(b.date);
		// Rules to push invalid dates to the bottom
		if (isValid(dateA) && !isValid(dateB)) return -1;
		if (!isValid(dateA) && isValid(dateB)) return 1;
		// Otherwise, sort by time descending
		return dateB - dateA;
	});

	const handleEmailClick = (emailId) => {
		dispatch(viewEmail(emailId));
	};

	const selectedEmail = emails.find((e) => e.id === selectedEmailId);

	// Function to strip HTML for previews
	const stripHtml = (html) => {
		const doc = new DOMParser().parseFromString(html, "text/html");
		return doc.body.textContent || "";
	};

	if (isViewingEmail && selectedEmail) {
		const senderName =
			typeof selectedEmail.sender === "object" && selectedEmail.sender.name
				? selectedEmail.sender.name
				: typeof selectedEmail.sender === "string"
				? selectedEmail.sender
				: "Unknown Sender";
		const senderInitial = senderName ? senderName.charAt(0).toUpperCase() : "?";
		const emailDate = selectedEmail.date ? parseISO(selectedEmail.date) : null;
		const formattedDate = emailDate && isValid(emailDate) ? format(emailDate, "MMMM d, yyyy 'at' h:mm a") : "Yesterday";


		return (
			// key on the email id forces React to fully remount the detail view when
			// switching emails, so the previous email's header/body subtree is torn
			// down instead of lingering/stacking above the current one.
			<div key={selectedEmail.id} className="h-full w-full bg-white font-inter flex flex-col">
				{/* Header for the full email view */}
				<div className="p-2 border-b border-gray-200 flex items-center justify-between">
					<button
						onClick={() => dispatch(setIsViewingEmail(false))}
						className="text-blue-500 text-base flex items-center"
					>
						<ChevronLeft className="w-6 h-6" />
						<span className="font-semibold">Back</span>
					</button>
					
				</div>

				{/* Email Content */}
				<div className="p-4 flex-grow overflow-y-auto no-scrollbar">
					<div className="flex items-center mb-6">
						<div
							className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-white"
							style={{ backgroundColor: getColorForLetter(senderInitial) }}
						>
							{senderInitial}
						</div>
						<div className="ml-3 flex-grow">
							<p className="font-semibold text-base">{senderName}</p>
							<p className="text-sm text-gray-500">To: Me</p>
						</div>
						<p className="text-sm text-gray-500 self-start">{formattedDate.split(' at ')[1]}</p>
					</div>

					<h1 className="text-xl font-bold mb-4">{selectedEmail.subject}</h1>

					<div className="text-base leading-relaxed email-body">
						{renderEmailBody(selectedEmail.body, dispatch, brandConfig)}
					</div>

					{/* Attachment Section */}
					{selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
						<div className="mt-8">
							<h3 className="text-base font-semibold border-b pb-2 mb-3 text-gray-600">
								{selectedEmail.attachments.length} Attachment
								{selectedEmail.attachments.length > 1 ? "s" : ""}
							</h3>
							<div className="space-y-3">
								{selectedEmail.attachments.map((attachment, index) => {
									const isImage = attachment.mime_type && attachment.mime_type.startsWith('image/');
									const isHtml = attachment.mime_type && attachment.mime_type === 'text/html';
									const hasBase64 = !!attachment.base64_content;
									const hasHtml = !!attachment.html_content;
									const hasUrl = !!attachment.url;
									const filename = attachment.filename || attachment.name || 'Attachment';
									const commonClass = "w-full flex items-center p-2 border rounded-lg bg-gray-50 text-blue-500 hover:underline";
									const blocks = [];
									// Image: open in new window on click
									if (isImage && hasBase64) {
										blocks.push(
											<button
												key={index + '-img'}
												className={commonClass}
												style={{ textAlign: 'left' }}
												onClick={() => {
													const newWindow = window.open();
													if (newWindow) {
														newWindow.document.write(`<img src='data:${attachment.mime_type};base64,${attachment.base64_content}' alt='${filename}' style='max-width:100%;height:auto;' />`);
														newWindow.document.title = filename;
													}
												}}
											>
												<Paperclip className="w-4 h-4 mr-2" />
												<span>{filename}</span>
											</button>
										);
									}
									// HTML: open in new window
									if (isHtml && hasHtml) {
										blocks.push(
											<button
												key={index + '-html'}
												className={commonClass}
												style={{ textAlign: 'left' }}
												onClick={() => {
													const newWindow = window.open();
													if (newWindow) {
														newWindow.document.write(attachment.html_content);
														newWindow.document.title = filename;
													}
												}}
											>
												<Paperclip className="w-4 h-4 mr-2" />
												<span>{filename}</span>
											</button>
										);
									}
									// URL: open in new window
									if (hasUrl) {
										blocks.push(
											<a
												key={index + '-url'}
												href={attachment.url}
												target="_blank"
												rel="noopener noreferrer"
												className={commonClass}
												style={{ textAlign: 'left' }}
											>
												<Paperclip className="w-4 h-4 mr-2" />
												<span>{filename}</span>
											</a>
										);
									}
									// Base64: always offer download (except images already handled above)
									if (hasBase64 && !(isImage && hasBase64)) {
										blocks.push(
											<a
												key={index + '-download'}
												href={`data:${attachment.mime_type};base64,${attachment.base64_content}`}
												download={filename}
												className={commonClass}
												style={{ textAlign: 'left' }}
											>
												<Paperclip className="w-4 h-4 mr-2" />
												<span>{filename}</span>
											</a>
										);
									}
									// If nothing else, just show the filename
									if (blocks.length === 0) {
										blocks.push(
											<div key={index + '-filename'} className={commonClass + ' text-gray-500'} style={{ textAlign: 'left' }}>
												<Paperclip className="w-4 h-4 mr-2" />
												{filename}
											</div>
										);
									}
									return <React.Fragment key={index}>{blocks}</React.Fragment>;
								})}
							</div>
						</div>
					)}
				</div>
			</div>
		);
	}

	return (
		<div className="bg-white h-full flex flex-col font-inter">
			{/* Header */}
			<div className="flex items-center justify-center p-4 border-b relative">
				<h1 className="font-semibold text-lg">{meta.title || "Inbox"}</h1>
			</div>

			{/* Email List */}
			<div
				ref={emailListRef}
				className="flex-1 overflow-y-auto no-scrollbar divide-y divide-gray-200"
				style={{
					cursor: "grab",
					userSelect: "auto",
					scrollbarWidth: "none" /* For Firefox */,
				}}
			>
				{sortedEmails.map((email) => {
					const senderName =
						typeof email.sender === "object" && email.sender.name
							? email.sender.name
							: typeof email.sender === "string"
							? email.sender
							: "Unknown Sender";
					const senderInitial = senderName
						? senderName.charAt(0).toUpperCase()
						: "?";
					const emailDate = email.date ? parseISO(email.date) : null;
					const isNew = email.id === newlyReceivedEmailId;

					// Format date, handle invalid dates gracefully
					const formattedDate =
						emailDate && isValid(emailDate)
							? format(emailDate, "h:mm a")
							: "Invalid date";

					return (
						<div
							key={email.id}
							data-email-id={email.id}
							onClick={() => handleEmailClick(email.id)}
							className={`p-4 flex items-start space-x-4 cursor-pointer transition-colors duration-300 ${
								selectedEmailId === email.id
									? "bg-blue-100"
									: "hover:bg-gray-50"
							} ${isNew ? "new-email-highlight" : ""}`}
						>
							<div
								className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-white"
								style={{ backgroundColor: getColorForLetter(senderInitial) }}
							>
								{senderInitial}
							</div>
							<div className="flex-1 min-w-0">
								<div className="flex justify-between items-baseline">
									<p
										className={`font-semibold truncate ${
											!email.read ? "text-gray-900" : "text-gray-600"
										}`}
									>
										{senderName}
									</p>
									<p
										className={`text-xs flex-shrink-0 ml-2 ${
											!email.read ? "text-blue-500 font-bold" : "text-gray-500"
										}`}
									>
										{formattedDate}
									</p>
								</div>
								<p
									className={`font-semibold truncate ${
										!email.read ? "text-gray-800" : "text-gray-600"
									}`}
								>
									{email.subject}
								</p>
								<div className="flex items-center">
									<p className="text-gray-500 truncate flex-grow">
										{stripHtml(email.body)}
									</p>
									{email.attachments && email.attachments.length > 0 && (
										<Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
									)}
								</div>
							</div>
							{!email.read && (
								<div className="w-2.5 h-2.5 bg-blue-500 rounded-full self-center ml-2 flex-shrink-0"></div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}

// Add global CSS for email-body links
const styles = `
.truncate-2-lines {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
.email-body a {
  color: #2563eb;
  text-decoration: underline;
  cursor: pointer;
}
`;

// Inject styles into the document head
const styleSheet = document.createElement("style");
styleSheet.innerHTML = styles;
document.head.appendChild(styleSheet);
